import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import _ from 'lodash';
import numeri from 'numeri';
import * as d3 from 'd3';
import s from './Entropy.css';
import { MenuBase, defaultOptWidth } from '../../core/menuUtils';

const n = 100;
const m = 20;
const xDim = 700;
const yDim = 450;
const leftPadding = 115;
const bottomPadding = 50;
const topPadding = 60;
const intraPadding = 30;
const padding = 10;
const innerYDim = yDim - topPadding - bottomPadding;
const plotWidth = 400;
const plotHeight = (innerYDim - intraPadding) / 2;
const plotOffset = plotHeight + intraPadding;
const gtOffset = 90;
const mlOffset = 250;
const mmOffset = 270;
const jkOffset = 290;
const entropyNameXOffset = 5;
const entropyXOffset = 110;
const decimals = 3;
const yLabelX = 40;
const descLeftPadding = leftPadding + plotWidth + padding;
const descTransform = `translate(${descLeftPadding}, ${topPadding})`;
const xScale = d3.scaleLinear()
  .domain([0, m])
  .range([0, plotWidth]);
const poissonLambda = 10.0;
const lomaxAlpha = 1.0;
const lomaxScale = 5.0;

function poissonPmf() {
  const mult = Math.exp(-poissonLambda);
  let factorial = 1;
  const pmf = numeri.range(m)
    .map((i) => {
      const res = mult * poissonLambda ** i / factorial;
      factorial *= (i + 1);
      return res;
    });
  const sum = pmf.sum();
  return pmf.mapInPlace((x) => x / sum);
}

function lomaxPmf() {
  const cdf = numeri.range(m + 1)
    .map((i) => {
      return 1 - (1 + i / lomaxScale) ** (-lomaxAlpha);
    });
  const sum = cdf.get([m]);
  cdf.mapInPlace((x) => x / sum);

  return cdf.slice([1]).minus(cdf.slice([0, m]));
}

const distributionOpts = [
  {
    text: 'uniform',
    pmf: numeri.fill([m], 1 / m),
  },
  {
    text: 'poisson',
    pmf: poissonPmf(),
  },
  {
    text: 'lomax',
    pmf: lomaxPmf(),
  },
];

function genSamples(distIdx) {
  const pmf = distributionOpts[distIdx].pmf;
  let sum = 0;
  const cdf = pmf.data.map((x) => {
    sum += x;
    return sum;
  });

  return numeri.empty([n])
    .map(() => {
      const percentile = Math.random();
      let idx = 0;
      while (idx < m && cdf[idx] < percentile) {
        idx += 1;
      }
      return idx;
    });
}

function calcMle(counts) {
  const n_ = counts.sum();
  const ps = counts.div(n_);
  const hs = ps.map((p) => p > 0 ? Math.log(p) * p : 0);
  return -hs.sum();
}

class Entropy extends MenuBase {
  constructor(props) {
    super(props, { s, xDim, yDim });
    this.distributionIdx = 0;
  }

  shouldComponentUpdate() {
    return false;
  }

  componentDidMount() {
    this.draw();
  }

  precompute() {
    this.pmf = distributionOpts[this.distributionIdx].pmf;
    this.expected = this.pmf.times(n);

    this.samples = genSamples(this.distributionIdx);
    this.counts = numeri.zeros([m]);
    this.samples.data.forEach((x) => this.counts.update([x], (k) => k + 1));

    this.maxCount = numeri.concat([this.counts, this.expected], 0)
      .max();
    this.maxPmf = this.maxCount / n;
  }

  draw() {
    const svg = d3.select('#entropy');
    if (this.mutable) {
      this.mutable.remove();
    }

    this.mutable = svg.append('g');
    this.mutableDesc = this.mutable.append('g')
      .attr('transform', descTransform);

    this.precompute();
    this.drawSettings();
    this.drawPlot(0, this.expected, 'pmf');
    this.drawPlot(plotOffset, this.counts, 'empirical');
    this.drawEntropy();
  }

  drawSettings() {
    this.drawOptions({
      g: this.mutable,
      title: 'distribution',
      x: leftPadding,
      y: 0,
      opts: distributionOpts,
      selectedIdx: this.distributionIdx,
      onSelect: (i) => {
        this.distributionIdx = i;
        this.draw();
      },
    });

    this.drawOptions({
      g: this.mutable,
      title: '',
      x: leftPadding + defaultOptWidth * (distributionOpts.length + 1),
      y: 0,
      opts: [{ text: 'resample!' }],
      selectedIdx: -1,
      onSelect: () => {
        this.draw();
      },
    });
  }

  drawPlot(yOffset, expected, className) {
    const box = this.mutable.append('g')
      .attr('transform', `translate(${leftPadding}, ${topPadding + yOffset})`);

    const yScale = d3.scaleLinear()
      .domain([0, this.maxCount])
      .range([plotHeight, 0]);
    const pmfScale = d3.scaleLinear()
      .domain([0, this.maxPmf])
      .range([plotHeight, 0]);

    const blockExpected = _.flatMap(expected.data, (count, x) => [
      { count, x },
      { count, x: x + 1 },
    ]);

    const areaFn = d3.area()
      .x((d) => xScale(d.x))
      .y0(() => yScale(0))
      .y1((d) => yScale(d.count));
    box.append('path')
      .datum(blockExpected)
      .attr('class', s[className])
      .attr('d', areaFn);

    box.append('g')
      .attr('transform', `translate(0, ${plotHeight})`)
      .call(d3
        .axisBottom()
        .scale(xScale)
        .tickValues(numeri.range(m + 1).data),
      );
    box.append('g')
      .call(d3.axisLeft().scale(pmfScale));
  }

  drawEntropy() {
    const descG = this.mutableDesc;

    const pmf = distributionOpts[this.distributionIdx].pmf;
    const gt = calcMle(pmf);
    const ml = calcMle(this.counts);
    const mm = ml + m / (2 * n);
    const jkSum = numeri.range(m)
      .map((j) => {
        const binCount = this.counts.get([j]);
        const counts = this.counts.copy();
        counts.update([j], (k) => k - 1);
        return binCount * calcMle(counts);
      })
      .sum();
    const jk = n * ml - (n - 1) / n * jkSum;
    const bestEmpiricalIdx = numeri.vector([ml, mm, jk])
      .minus(gt)
      .mapInPlace(Math.abs)
      .argmin();
    [
      [gtOffset, gt],
      [mlOffset, ml],
      [mmOffset, mm],
      [jkOffset, jk],
    ].forEach(([offset, entropy], i) => {
      const bits = entropy / Math.log(2);
      descG.append('text')
        .attr('class', i === bestEmpiricalIdx + 1 ? s.bestdesc : s.desc)
        .attr('x', entropyXOffset)
        .attr('y', offset)
        .text(bits.toFixed(decimals));
    });
  }

  render() {
    return (
      <svg width={xDim} height={yDim} id="entropy">
        <text className={s.label} x={yLabelX} y={topPadding + plotHeight / 2 - 8}>
          true
        </text>
        <text className={s.label} x={yLabelX} y={topPadding + plotHeight / 2 + 8}>
          probability
        </text>
        <text className={s.label} x={yLabelX} y={topPadding + plotOffset + plotHeight / 2 - 16}>
          empirical
        </text>
        <text className={s.label} x={yLabelX} y={topPadding + plotOffset + plotHeight / 2}>
          probability
        </text>
        <text className={s.label} x={yLabelX} y={topPadding + plotOffset + plotHeight / 2 + 16}>
           (n={n})
        </text>
        <text
          className={s.label}
          x={leftPadding + plotWidth / 2}
          y={2 * plotOffset + topPadding + 5}
        >
          x
        </text>
        <text className={s.label} x={descLeftPadding + 77} y={topPadding + 5}>
          entropy estimate / bits
        </text>
        <g id="desc" transform={descTransform}>
          <rect className={s.descrect} y={15} width={155} height={plotHeight - 15} />
          <rect className={s.descrect} y={plotOffset} width={155} height={plotHeight} />
          <text className={s.desc} x={entropyNameXOffset} y={gtOffset}>
            exact:
          </text>
          <text className={s.desc} x={entropyNameXOffset} y={mlOffset}>
            naive:
          </text>
          <text className={s.desc} x={entropyNameXOffset} y={mmOffset}>
            Miller-Madow:
          </text>
          <text className={s.desc} x={entropyNameXOffset} y={jkOffset}>
            jackknife:
          </text>
        </g>
      </svg>
    );
  }
}

export default withStyles(s)(Entropy);



// WEBPACK FOOTER //
// src/components/Entropy/Entropy.js